import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactDOM from 'react-dom';
import { ChevronDown, FunnelPlus, Plus, Search } from 'lucide-react';
import { BoardSearchSimplePickerPanel } from '../../board/board-search-filters/BoardSearchSimplePickerPanel';
import {
  PeriodModal,
  getCalendarBounds,
} from '../../equipment-consult/components/EquipmentConsultProductionPeriodPicker';
import '../../equipment-consult/components/EquipmentConsultProductionPeriodPicker.css';
import '../../board/board-search-filters/BoardSearchFilters.css';
import { fetchTeamAgents } from '../services/boardChatService';
import {
  EMPTY_RESOLVED_FILTERS,
  applyResolvedFilters,
  countResolvedFilters,
  periodLabel,
  searchConversationsForText,
  uniqueAssigneesFromConversations,
  uniqueContactsFromConversations,
} from '../utils/boardChatResolvedFilterUtils';

const FILTER_DEFS = [
  { key: 'period', label: 'Período' },
  { key: 'protocol', label: 'Protocolo' },
  { key: 'user', label: 'Usuário' },
  { key: 'text', label: 'Texto na conversa' },
  { key: 'assignee', label: 'Quem atendeu' },
];

const POPOVER_CLASS =
  'board-search-filters-popover board-search-filters-popover--simple board-search-filters-popover--tecnico bch-resolved-filter-popover';

function chipCount(filters, key) {
  if (key === 'period') return filters.period?.startDate && filters.period?.endDate ? 1 : 0;
  if (key === 'protocol') return String(filters.protocol || '').trim() ? 1 : 0;
  if (key === 'user') return (filters.userIds || []).length;
  if (key === 'text') return String(filters.text || '').trim() ? 1 : 0;
  if (key === 'assignee') return (filters.assigneeIds || []).length;
  return 0;
}

function FilterChip({ chip, isActive, chipRefs, onSelectFilter, count }) {
  return (
    <div
      className="board-search-filters-chip-anchor"
      ref={(el) => {
        if (el) chipRefs.current[chip.key] = el;
        else delete chipRefs.current[chip.key];
      }}
    >
      <button
        type="button"
        className={[
          'board-search-filters-chip',
          isActive ? 'is-active' : '',
          count > 0 ? 'has-selection' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        aria-label={`Filtrar por ${chip.label}`}
        aria-expanded={isActive}
        onClick={() => onSelectFilter(chip.key)}
      >
        <Plus
          size={11}
          strokeWidth={1.75}
          className="board-search-filters-chip-icon board-search-filters-chip-icon--plus"
          aria-hidden
        />
        <span className="board-search-filters-chip-label">{chip.label}</span>
        {count > 0 ? (
          <span className="board-search-filters-chip-count">{count}</span>
        ) : null}
        <ChevronDown
          size={11}
          strokeWidth={1.75}
          className={[
            'board-search-filters-chip-icon',
            'board-search-filters-chip-icon--chevron',
            isActive ? 'is-open' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          aria-hidden
        />
      </button>
    </div>
  );
}

function usePopoverStyle(anchorRect, width = 260, maxHeight = 320) {
  return useMemo(() => {
    if (!anchorRect) return null;
    const margin = 8;
    const spaceBelow = window.innerHeight - anchorRect.bottom - margin;
    const spaceAbove = anchorRect.top - margin;
    const flipped = spaceBelow < 220 && spaceAbove > spaceBelow;
    const available = flipped ? spaceAbove : spaceBelow;
    const height = Math.min(maxHeight, Math.max(160, available));
    const top = flipped ? anchorRect.top - height - 4 : anchorRect.bottom + 4;
    let left = anchorRect.left;
    if (left + width > window.innerWidth - margin) {
      left = window.innerWidth - width - margin;
    }
    left = Math.max(margin, left);
    return {
      position: 'fixed',
      top,
      left,
      width,
      boxSizing: 'border-box',
      zIndex: 10070,
      maxHeight: height,
      '--bsf-cidade-popover-max-h': `${height}px`,
    };
  }, [anchorRect, width, maxHeight]);
}

function ListFilterPopover({
  fieldKey,
  listAriaLabel,
  items,
  selected,
  anchorRect,
  onToggle,
  onToggleAll,
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const popoverStyle = usePopoverStyle(anchorRect);

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return items;
    return items.filter((i) => String(i.label || '').toLowerCase().includes(q));
  }, [items, searchTerm]);

  if (!anchorRect || !popoverStyle) return null;

  return ReactDOM.createPortal(
    <div
      data-bch-resolved-filter-popover={fieldKey}
      className={POPOVER_CLASS}
      style={popoverStyle}
      role="dialog"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <BoardSearchSimplePickerPanel
        searchPlaceholder="Buscar"
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        items={filtered}
        getKey={(item) => item.value}
        getLabel={(item) => item.label}
        selectedKeys={selected}
        onToggle={(item) => onToggle(item.value)}
        emptyMessage="Nenhum resultado"
        listAriaLabel={listAriaLabel}
        bulkMenuEnabled
        onBulkToggleAll={
          filtered.length > 0
            ? () => {
                const keys = filtered.map((i) => i.value);
                const allMarked = keys.length > 0 && keys.every((k) => selected.includes(k));
                onToggleAll(keys, allMarked);
              }
            : undefined
        }
      />
    </div>,
    document.body
  );
}

function InlineProtocolPanel({ value, onChange }) {
  const [draft, setDraft] = useState(value || '');

  useEffect(() => {
    setDraft(value || '');
  }, [value]);

  return (
    <div className="bch-resolved-filter-inline" data-bch-resolved-filter-inline="protocol">
      <label className="bch-resolved-filter-field">
        <span className="bch-resolved-filter-field-label">ID da conversa</span>
        <input
          type="text"
          className="bch-resolved-filter-input"
          value={draft}
          placeholder="Ex.: 12345"
          inputMode="numeric"
          autoFocus
          onChange={(e) => setDraft(e.target.value.replace(/[^\d]/g, ''))}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              onChange(String(draft || '').trim());
            }
          }}
        />
      </label>
      <div className="bch-resolved-filter-actions">
        <button
          type="button"
          className="bch-resolved-filter-btn bch-resolved-filter-btn--ghost"
          onClick={() => {
            setDraft('');
            onChange('');
          }}
        >
          Limpar
        </button>
        <button
          type="button"
          className="bch-resolved-filter-btn bch-resolved-filter-btn--primary"
          onClick={() => onChange(String(draft || '').trim())}
        >
          Aplicar
        </button>
      </div>
    </div>
  );
}

function InlineTextPanel({ value, conversations, onChange, onOpenConversation }) {
  const [draft, setDraft] = useState(value || '');
  const results = useMemo(
    () => searchConversationsForText(conversations, draft),
    [conversations, draft]
  );

  useEffect(() => {
    setDraft(value || '');
  }, [value]);

  return (
    <div className="bch-resolved-filter-inline" data-bch-resolved-filter-inline="text">
      <label className="bch-resolved-filter-field">
        <span className="bch-resolved-filter-field-label">Buscar na conversa</span>
        <div className="bch-resolved-filter-search">
          <Search size={13} strokeWidth={1.8} aria-hidden />
          <input
            type="text"
            className="bch-resolved-filter-input"
            value={draft}
            placeholder="Digite para buscar…"
            autoFocus
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                onChange(String(draft || '').trim());
              }
            }}
          />
        </div>
      </label>
      <div className="bch-resolved-filter-results" role="listbox" aria-label="Resultados">
        {!String(draft || '').trim() ? (
          <p className="bch-resolved-filter-empty">Digite para ver conversas.</p>
        ) : results.length === 0 ? (
          <p className="bch-resolved-filter-empty">Nenhum resultado.</p>
        ) : (
          results.map((item) => (
            <button
              key={item.value}
              type="button"
              className="bch-resolved-filter-result"
              role="option"
              onClick={() => {
                onChange(String(draft || '').trim());
                onOpenConversation?.(Number(item.value));
              }}
            >
              <span className="bch-resolved-filter-result-title">{item.label}</span>
              <span className="bch-resolved-filter-result-hint">{item.hint}</span>
              <span className="bch-resolved-filter-result-id">#{item.value}</span>
            </button>
          ))
        )}
      </div>
      <div className="bch-resolved-filter-actions">
        <button
          type="button"
          className="bch-resolved-filter-btn bch-resolved-filter-btn--ghost"
          onClick={() => {
            setDraft('');
            onChange('');
          }}
        >
          Limpar
        </button>
        <button
          type="button"
          className="bch-resolved-filter-btn bch-resolved-filter-btn--primary"
          onClick={() => onChange(String(draft || '').trim())}
        >
          Filtrar lista
        </button>
      </div>
    </div>
  );
}

/**
 * Filtros da caixa Resolvidas — período, protocolo, usuário, texto e quem atendeu.
 */
export function BoardChatResolvedFilters({
  conversations = [],
  filters = EMPTY_RESOLVED_FILTERS,
  onChange,
  teamId,
  onOpenConversation,
  tipProps,
}) {
  const anchorRef = useRef(null);
  const chipRefs = useRef({});
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeFilterKey, setActiveFilterKey] = useState(null);
  const [anchorRect, setAnchorRect] = useState(null);
  const [floatingStyle, setFloatingStyle] = useState({ top: 0, left: 0 });
  const [teamAgents, setTeamAgents] = useState([]);

  const activeCount = countResolvedFilters(filters);
  const contactItems = useMemo(
    () => uniqueContactsFromConversations(conversations),
    [conversations]
  );
  const textSearchPool = useMemo(
    () => applyResolvedFilters(conversations, { ...filters, text: '' }),
    [conversations, filters]
  );
  const assigneeItems = useMemo(() => {
    const fromConvs = uniqueAssigneesFromConversations(conversations);
    const map = new Map(fromConvs.map((i) => [i.value, i]));
    for (const a of teamAgents) {
      const id = a?.id ?? a?.user_id;
      if (id == null) continue;
      const key = String(id);
      if (map.has(key)) continue;
      map.set(key, {
        value: key,
        label: String(a.name || a.available_name || a.email || `#${key}`).trim(),
      });
    }
    return [...map.values()].sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));
  }, [conversations, teamAgents]);

  useEffect(() => {
    if (!menuOpen || !teamId) return undefined;
    let cancelled = false;
    fetchTeamAgents(teamId)
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
        setTeamAgents(list);
      })
      .catch(() => {
        if (!cancelled) setTeamAgents([]);
      });
    return () => {
      cancelled = true;
    };
  }, [menuOpen, teamId]);

  const updateFloatingPosition = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const menuWidth = Math.min(420, window.innerWidth - 32);
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - menuWidth - 8));
    const top = rect.bottom + 6;
    setFloatingStyle({ top, left });
  }, []);

  useLayoutEffect(() => {
    if (!menuOpen) return;
    updateFloatingPosition();
  }, [menuOpen, activeFilterKey, updateFloatingPosition]);

  useLayoutEffect(() => {
    if (!activeFilterKey || activeFilterKey === 'protocol' || activeFilterKey === 'text') {
      setAnchorRect(null);
      return;
    }
    const el = chipRefs.current[activeFilterKey];
    setAnchorRect(el ? el.getBoundingClientRect() : null);
  }, [activeFilterKey, menuOpen, filters]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onResize = () => updateFloatingPosition();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
    };
  }, [menuOpen, updateFloatingPosition]);

  useEffect(() => {
    if (!menuOpen && !activeFilterKey) return undefined;
    const onDoc = (e) => {
      const t = e.target;
      if (anchorRef.current?.contains(t)) return;
      if (t.closest?.('[data-bch-resolved-filter-popover]')) return;
      if (t.closest?.('.equipment-consult-period-modal')) return;
      if (t.closest?.('.ec-period-nav')) return;
      const inFloating = t.closest?.('.board-search-filters-floating');
      const inActiveChip = activeFilterKey && chipRefs.current[activeFilterKey]?.contains(t);
      if (inActiveChip) return;
      if (inFloating) return;
      setActiveFilterKey(null);
      setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDoc, true);
    return () => document.removeEventListener('mousedown', onDoc, true);
  }, [menuOpen, activeFilterKey]);

  const handleSelectFilter = (key) => {
    if (key === 'clearAll') {
      onChange?.({ ...EMPTY_RESOLVED_FILTERS });
      setActiveFilterKey(null);
      return;
    }
    setActiveFilterKey((prev) => (prev === key ? null : key));
  };

  const patch = useCallback(
    (partial) => onChange?.({ ...filters, ...partial }),
    [filters, onChange]
  );

  const handleToggleList = useCallback(
    (stateKey, value) => {
      const current = filters[stateKey] || [];
      const next = current.includes(value)
        ? current.filter((x) => x !== value)
        : [...current, value];
      patch({ [stateKey]: next });
    },
    [filters, patch]
  );

  const handleToggleAll = useCallback(
    (stateKey, keys, allMarked) => {
      const current = filters[stateKey] || [];
      const next = allMarked
        ? current.filter((x) => !keys.includes(x))
        : Array.from(new Set([...current, ...keys]));
      patch({ [stateKey]: next });
    },
    [filters, patch]
  );

  const bounds = getCalendarBounds();
  const periodAnchorRef = useMemo(
    () => ({
      get current() {
        return chipRefs.current.period || null;
      },
    }),
    []
  );

  const floating =
    menuOpen &&
    ReactDOM.createPortal(
      <>
        <div className="board-search-filters-backdrop bch-resolved-filters-backdrop" aria-hidden />
        <div
          className="board-search-filters-floating bch-resolved-filters-floating"
          style={{ top: floatingStyle.top, left: floatingStyle.left }}
        >
          <div
            className="board-search-filters-menu bch-resolved-filters-menu"
            role="dialog"
            aria-label="Filtros de resolvidas"
          >
            <div
              className="board-search-filters-chips bch-resolved-filters-chips"
              role="group"
              aria-label="Filtros de resolvidas"
            >
              <div className="board-search-filters-chips-top">
                {activeCount > 0 ? (
                  <button
                    type="button"
                    className="board-search-filters-chip board-search-filters-chip--clear"
                    onClick={() => handleSelectFilter('clearAll')}
                  >
                    Limpar filtros
                  </button>
                ) : null}
                {FILTER_DEFS.map((chip) => (
                  <FilterChip
                    key={chip.key}
                    chip={chip}
                    isActive={activeFilterKey === chip.key}
                    chipRefs={chipRefs}
                    onSelectFilter={handleSelectFilter}
                    count={chipCount(filters, chip.key)}
                  />
                ))}
              </div>
            </div>

            {activeFilterKey === 'protocol' ? (
              <InlineProtocolPanel
                value={filters.protocol}
                onChange={(protocol) => {
                  patch({ protocol });
                }}
              />
            ) : null}

            {activeFilterKey === 'text' ? (
              <InlineTextPanel
                value={filters.text}
                conversations={textSearchPool}
                onChange={(text) => patch({ text })}
                onOpenConversation={(id) => {
                  setMenuOpen(false);
                  setActiveFilterKey(null);
                  onOpenConversation?.(id);
                }}
              />
            ) : null}

            {filters.period?.startDate ||
            String(filters.protocol || '').trim() ||
            String(filters.text || '').trim() ? (
              <div className="bch-resolved-filters-summary" aria-live="polite">
                {filters.period?.startDate ? (
                  <span>Período: {periodLabel(filters.period)}</span>
                ) : null}
                {String(filters.protocol || '').trim() ? (
                  <span>Protocolo: #{String(filters.protocol).trim()}</span>
                ) : null}
                {String(filters.text || '').trim() ? (
                  <span>Texto: “{String(filters.text).trim()}”</span>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      </>,
      document.body
    );

  return (
    <>
      <div className="bch-resolved-filters-anchor" ref={anchorRef}>
        <button
          type="button"
          className={`bch-resolved-filters-btn${menuOpen ? ' is-open' : ''}${activeCount > 0 ? ' has-selection' : ''}`}
          aria-label="Filtros de resolvidas"
          aria-expanded={menuOpen}
          {...(!menuOpen && tipProps ? tipProps('Filtros') : {})}
          onClick={() => {
            setMenuOpen((o) => !o);
            setActiveFilterKey(null);
          }}
        >
          <FunnelPlus size={14} strokeWidth={1.9} aria-hidden />
          {activeCount > 0 ? (
            <span className="bch-resolved-filters-badge">{activeCount}</span>
          ) : null}
        </button>
      </div>
      {floating}
      {activeFilterKey === 'period' && anchorRect ? (
        <PeriodModal
          anchorRef={periodAnchorRef}
          anchorRect={anchorRect}
          currentPeriod={filters.period}
          minDate={bounds.minDate}
          maxDate={bounds.maxDate}
          maxRangeDays={730}
          hideHeader
          modalClassName="bch-resolved-period-modal"
          zIndex={10080}
          singleMonth
          onApply={(startDate, endDate) => {
            patch({ period: { startDate, endDate } });
            setActiveFilterKey(null);
          }}
          onClear={() => {
            patch({ period: null });
            setActiveFilterKey(null);
          }}
          onClose={() => setActiveFilterKey(null)}
        />
      ) : null}
      {activeFilterKey === 'user' ? (
        <ListFilterPopover
          fieldKey="user"
          listAriaLabel="Usuários"
          items={contactItems}
          selected={filters.userIds || []}
          anchorRect={anchorRect}
          onToggle={(value) => handleToggleList('userIds', value)}
          onToggleAll={(keys, allMarked) => handleToggleAll('userIds', keys, allMarked)}
        />
      ) : null}
      {activeFilterKey === 'assignee' ? (
        <ListFilterPopover
          fieldKey="assignee"
          listAriaLabel="Quem atendeu"
          items={assigneeItems}
          selected={filters.assigneeIds || []}
          anchorRect={anchorRect}
          onToggle={(value) => handleToggleList('assigneeIds', value)}
          onToggleAll={(keys, allMarked) => handleToggleAll('assigneeIds', keys, allMarked)}
        />
      ) : null}
    </>
  );
}
